"use client";

// Internal state, on a hotkey. See docs/PERSISTENT_CANVAS_ARCHITECTURE.md.

import { useEffect, useState } from "react";

import { getBackendIdentity, type BackendIdentity } from "@/lib/actions/backend-identity";
import {
  groupSceneElements,
  groupSceneRows,
  readSceneElements,
  type SceneElementRow,
} from "@/lib/datadog/stage-visibility";
import { FloatingPanel } from "@/ui/floating-panel";
import { SCENE_INSPECTOR_KEY } from "@/ui/scene-inspector";
import { debugToolsEnabled } from "@/lib/environment";
import { useUIStore } from "@/lib/stores/ui";
import { usePathPlanningWorkflowStore } from "@/lib/stores/path-planning-workflow";
import { useViewer3DStore } from "@/lib/stores/viewer-3d";

const REFRESH_MS = 1000;

type Inspector = { content: () => unknown };

function sceneElements(): SceneElementRow[] {
  if (typeof window === "undefined") return [];
  const inspector = (window as unknown as Record<string, Inspector | undefined>)[
    SCENE_INSPECTOR_KEY
  ];
  if (!inspector) return [];
  try {
    return groupSceneRows(
      groupSceneElements(
        readSceneElements(inspector.content() as Parameters<typeof readSceneElements>[0])
      )
    );
  } catch {
    return [];
  }
}

function Row({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className={warn ? "text-destructive font-semibold" : ""}>{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

function DebugPanelBody() {
  const [backend, setBackend] = useState<BackendIdentity | null>(null);
  const [elements, setElements] = useState<SceneElementRow[]>([]);

  const resolvedAsset = useViewer3DStore((state) => state.resolvedAsset);
  const visibleFileIds = useViewer3DStore((state) => state.visibleFileIds);
  const sliceFileId = useViewer3DStore((state) => state.sliceFileId);
  const workflowStatus = usePathPlanningWorkflowStore((state) => state.workflowStatus);
  const stepStatuses = usePathPlanningWorkflowStore((state) => state.stepStatuses);
  const visualizedStep = usePathPlanningWorkflowStore((state) => state.visualizedStep);
  const setDebugOpen = useUIStore((state) => state.setDebugOpen);

  useEffect(() => {
    void getBackendIdentity().then(setBackend);
  }, []);

  // The scene graph is not React state, so it is polled rather than subscribed to.
  useEffect(() => {
    const tick = () => setElements(sceneElements());
    tick();
    const timer = setInterval(tick, REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const ran = Object.entries(stepStatuses).filter(([, status]) => status !== "idle");

  return (
    // The panel is absolutely positioned by FloatingPanel, so it needs a viewport-sized
    // positioned ancestor that does not itself swallow clicks meant for the app.
    <div className="fixed inset-0 z-50 pointer-events-none">
      <FloatingPanel
        title="Debug"
        persistId="debug"
        defaultX={16}
        defaultY={96}
        width={340}
        onClose={() => setDebugOpen(false)}
        contentClassName="font-mono text-[11px] space-y-3"
      >
        <Section title="Backend">
          {backend ? (
            <>
              <Row label="app env" value={backend.appEnv} />
              <Row label="MAP" value={backend.mapHost ?? "unset"} />
              <Row label="namespace" value={backend.temporalNamespace ?? "unset"} />
              <Row label="queue" value={backend.taskQueue ?? "unset"} />
              <Row
                label="data / workers"
                value={`${backend.coherence.dataEnv} / ${backend.coherence.workerEnv}`}
                warn={!backend.coherence.coherent}
              />
              {!backend.coherence.coherent && (
                <div className="text-destructive">
                  MAP and the workers are different environments — a run will not find its project.
                </div>
              )}
            </>
          ) : (
            <div className="text-muted-foreground">unavailable</div>
          )}
        </Section>

        <Section title={`Scene (${elements.length})`}>
          {elements.length === 0 && <div className="text-muted-foreground">nothing mounted</div>}
          {/* Two lines rather than two columns: a label, its primitives and seven-digit counts
              cannot share this width, and wrapping split the label from its own suffix. */}
          {elements.map((element, index) => (
            <div key={`${element.label}-${index}`}>
              <div className={element.visible ? "" : "text-muted-foreground"}>
                {element.label}
                {element.visible ? "" : " \u00b7 hidden"}
                {!element.visible && element.hoverable ? " \u00b7 hoverable" : ""}
              </div>
              <div className="pl-3 break-words text-muted-foreground">
                {element.parts
                  .map(
                    (part) =>
                      `${part.count > 1 ? `${part.kind}\u00d7${part.count}` : part.kind} ${part.points.toLocaleString()}`
                  )
                  .join(" \u00b7 ")}
              </div>
            </div>
          ))}
        </Section>

        <Section title="Viewer">
          <Row
            label="resolved"
            value={
              resolvedAsset ? `${resolvedAsset.stage ?? "none"} / ${resolvedAsset.render}` : "none"
            }
          />
          <Row label="visible files" value={String(visibleFileIds.length)} />
          <Row label="slice file" value={sliceFileId ? "set" : "none"} />
        </Section>

        <Section title="Path run">
          <Row label="status" value={workflowStatus} />
          <Row label="visualized step" value={visualizedStep ?? "none"} />
          <Row
            label="steps"
            value={ran.length ? ran.map(([step, s]) => `${step}:${s}`).join(" ") : "all idle"}
          />
        </Section>
      </FloatingPanel>
    </div>
  );
}

/** Toggled from the command palette, which is the only way in. */
export function DebugPanel() {
  const open = useUIStore((state) => state.debugOpen);

  if (!debugToolsEnabled() || !open) return null;
  return <DebugPanelBody />;
}
